import { Link, LinkProps as RouterLinkPropsType } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { focusVisibleStyles, standardTransitionStyles } from '../../sharedStyles';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { Tooltip } from '../Tooltip/Tooltip';
import { Typography, StyledAs } from '../Typography/Typography';

// The button styles are all exported so they can be used directly
// in rare cases where the Button component cant be inherited from

export const baseButtonStyles = css`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  // for when the button has icon and text
  gap: 6px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: var(--border-radius-medium);
  ${standardTransitionStyles}

  ${focusVisibleStyles}
`;

export const disabledButtonStyles = css`
  &:disabled:not(.loading) {
    cursor: not-allowed;
    background: var(--grayscale-50);
    border-color: var(--grayscale-50);
    color: var(--grayscale-300);
  }
`;

export const disabledIconButtonStyles = css`
  &:disabled:not(.loading) {
    cursor: not-allowed;
    color: var(--grayscale-300);
  }
  &:disabled:not(.loading):hover {
    background: transparent;
    border-color: transparent;
  }
`;

export const disabledLinkButtonStyles = css`
  &:disabled:not(.loading) {
    cursor: not-allowed;
    color: var(--grayscale-300);
  }
`;

export const primaryButtonStyles = css`
  background: var(--purple-700);
  border: 1px solid var(--purple-700);
  color: var(--white);

  &:hover,
  &:focus-visible {
    // TODO(tokens): Replace legacy color token --medium-dark-purple-1 with numbered equivalent once available.
    background: var(--medium-dark-purple-1);
    // TODO(tokens): Replace legacy color token --medium-dark-purple-1 with numbered equivalent once available.
    border-color: var(--medium-dark-purple-1);
  }

  &:active {
    // TODO(tokens): Replace legacy color token --medium-dark-purple-2 with numbered equivalent once available.
    background: var(--medium-dark-purple-2);
    // TODO(tokens): Replace legacy color token --medium-dark-purple-2 with numbered equivalent once available.
    border-color: var(--medium-dark-purple-2);
  }

  // override other states when loading
  &.loading {
    cursor: not-allowed;
    background: var(--purple-700);
    border: 1px solid var(--purple-700);
    color: var(--white);
  }
`;

export const secondaryButtonStyles = css`
  background-color: var(--grayscale-100);
  border: 1px solid var(--grayscale-100);
  color: var(--grayscale-900);

  &:hover,
  &:focus-visible {
    background: var(--grayscale-200);
    border-color: var(--grayscale-200);
  }

  &:active {
    background: var(--grayscale-300);
    border-color: var(--grayscale-300);
  }

  // override other states when loading
  &.loading {
    cursor: not-allowed;
    background: var(--grayscale-100);
    border: 1px solid var(--grayscale-100);
    color: var(--grayscale-900);
  }
`;

export const tertiaryButtonStyles = css`
  background-color: transparent;
  border: 1px solid var(--grayscale-100);
  color: var(--grayscale-900);

  &:hover,
  &:focus-visible {
    background: var(--grayscale-50);
    border-color: var(--grayscale-50);
  }

  &:active {
    background: var(--grayscale-100);
    border-color: var(--grayscale-100);
  }

  // override other states when loading
  &.loading {
    cursor: not-allowed;
    background: transparent;
    border: 1px solid var(--grayscale-100);
    color: var(--grayscale-900);
  }
`;

export const linkButtonStyles = css`
  background: transparent;
  border: none;
  color: var(--grayscale-900);
  text-decoration: underline;
  padding: 0;
  height: fit-content;
  display: inline;
  justify-content: unset;
  align-items: unset;
  gap: unset;
`;

export const linkDottedButtonStyles = css`
  ${linkButtonStyles}
  text-decoration: underline dotted;
  text-underline-offset: 3px;
`;

export const largeButtonStyles = css`
  height: 44px;
  padding: 0 16px;
  width: fit-content;
`;

export const smallButtonStyles = css`
  height: 36px;
  padding: 0 12px;
`;

export const extraSmallButtonStyles = css`
  height: 32px;
  padding: 0 8px;
`;

export const fullWidthButtonStyles = css`
  width: 100%;
`;

export const hugWidthButtonStyles = css`
  width: fit-content;
`;

export const iconButtonStyles = css`
  ${tertiaryButtonStyles}

  // height: 44px;
  // width: 44px;
  border: 1px solid transparent;
  color: var(--grayscale-600);

  // override other states when loading
  &.loading {
    cursor: not-allowed;
    border: 1px solid transparent;
    color: var(--grayscale-600);
  }
`;

export const borderButtonStyles = css`
  border: 1px solid var(--grayscale-100);
`;

export const largeIconButtonStyles = css`
  height: 44px;
  width: 44px;
`;

export const smallIconButtonStyles = css`
  height: 36px;
  width: 36px;
`;

export const highlightedButtonStyles = css`
  background: var(--purple-200);
  border: 1px solid var(--purple-200);
  color: var(--grayscale-900);

  &:hover,
  &:focus-visible,
  &:active,
  &.loading {
    background: var(--purple-200);
    border-color: var(--purple-200);
  }

  &.loading {
    cursor: not-allowed;
  }
`;

export const formScrollButtonStyles = css`
  background: var(--yellow-100);
  border: 1px solid var(--yellow-100);
  color: var(--grayscale-900);

  &:hover,
  &:focus-visible {
    background: var(--yellow-300);
    border-color: var(--yellow-300);
  }

  &:active {
    // TODO(tokens): Replace legacy color token --dark-yellow with numbered equivalent once available.
    background: var(--dark-yellow);
    // TODO(tokens): Replace legacy color token --dark-yellow with numbered equivalent once available.
    border-color: var(--dark-yellow);
  }

  // override other states when loading
  &.loading {
    cursor: not-allowed;
    background: var(--yellow-100);
    border: 1px solid var(--yellow-100);
    color: var(--grayscale-900);
  }
`;

// define the props as lists to to make it more efficient to render
// the different combos in the story tests
export const buttonVariants = [
  'primary',
  'secondary',
  'tertiary',
  'link',
  'linkDotted',
  'highlighted',
  'formScroll',
] as const;
export const buttonSizes = ['lg', 'sm', 'xs'] as const;
export const buttonWidths = ['full', 'hug'] as const;

// convert the prop lists to types
export type ButtonVariant = (typeof buttonVariants)[number];
type ButtonSize = (typeof buttonSizes)[number];
type ButtonWidth = (typeof buttonWidths)[number];

type StyledButtonProps = {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $width: ButtonWidth;
};

// TODO: replace usage of this with the Button component
export const RouterLink = styled(Link)`
  ${linkButtonStyles}
`;

type IconButtonVariant = 'default' | 'largeBorderButton';

const StyledIconButton = styled.button<{ $size: ButtonSize; $variant: IconButtonVariant }>`
  ${baseButtonStyles}
  ${iconButtonStyles}
  ${({ $size }) => $size === 'sm' && smallIconButtonStyles}
  ${({ $size }) => $size === 'lg' && largeIconButtonStyles}
  ${disabledIconButtonStyles}
  ${({ $variant }) =>
    $variant === 'largeBorderButton' &&
    css`
      ${borderButtonStyles}${largeButtonStyles}
    `}
`;

export interface BaseIconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The content of the button.
   */
  children?: React.ReactNode;
  /**
   * What element to render the button as.
   * Default is `"button"`.
   */
  renderedAs?: string | React.ElementType;
  className?: string;
  size?: ButtonSize;
  /**
   * Whether to show a loading spinner instead of the `children`.
   */
  isLoading?: boolean;
}

export type IconButtonProps = BaseButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>;
export type IconHtmlLinkProps = BaseButtonProps & React.AnchorHTMLAttributes<HTMLAnchorElement>;
export type IconRouterLinkProps = BaseButtonProps & RouterLinkPropsType;

/**
 * Icon version of the button component.
 *
 * This is a separate component instead of a variant because it doesn't have the width prop that the other button variants have.
 *
 * Will render as:
 * - a react-router `Link` if `to` prop is provided.
 * - an `a` if `href` prop is provided.
 * - a `button` if no `to` or `href` prop is provided.
 */
export const IconButton = ({
  children,
  className = '',
  size = 'lg',
  isLoading = false,
  iconButtonVariant = 'default',
  ...rest
}: IconButtonProps | IconHtmlLinkProps | IconRouterLinkProps) => {
  const isRouterLink = 'to' in rest;
  const isHtmlLink = 'href' in rest;

  return (
    <StyledIconButton
      $variant={iconButtonVariant}
      as={isRouterLink ? Link : isHtmlLink ? 'a' : 'button'}
      $size={size}
      className={`${isLoading ? 'loading' : ''} ${className}`}
      {...rest}
    >
      {/* TODO: make loading spinner 20x20px in buttons */}
      {isLoading ? <LoadingSpinner height="fit-content" /> : children}
    </StyledIconButton>
  );
};

const StyledButton = styled(Typography)<StyledButtonProps>`
  ${baseButtonStyles}

  ${({ $size }) => $size === 'sm' && smallButtonStyles}
  ${({ $size }) => $size === 'lg' && largeButtonStyles}
  ${({ $size }) => $size === 'xs' && extraSmallButtonStyles}
  // TODO: ask about why fit-content was added to largeButtonStyles
  ${({ $width }) => $width === 'full' && fullWidthButtonStyles}
  ${({ $width }) => $width === 'hug' && hugWidthButtonStyles}
  // variants have to go after sizes so the link styles can override the sizes
  ${({ $variant }) => $variant === 'primary' && primaryButtonStyles}
  ${({ $variant }) => $variant === 'secondary' && secondaryButtonStyles}
  ${({ $variant }) => $variant === 'tertiary' && tertiaryButtonStyles}
  ${({ $variant }) => $variant === 'link' && linkButtonStyles}
  ${({ $variant }) => $variant === 'linkDotted' && linkDottedButtonStyles}
  ${({ $variant }) => $variant === 'highlighted' && highlightedButtonStyles}
  ${({ $variant }) => $variant === 'formScroll' && formScrollButtonStyles}

  ${({ $variant }) =>
    $variant === 'link' || $variant === 'linkDotted' ? disabledLinkButtonStyles : disabledButtonStyles}
`;

export type BaseButtonProps = {
  /**
   * The content of the button.
   */
  children?: React.ReactNode;
  className?: string;
  /**
   * The color theme of the button.
   * `"primary"` = purple; `"secondary"` = gray; `"tertiary"` = transparent with outline
   * Default is `"primary"`.
   */
  variant?: ButtonVariant;
  size?: ButtonSize;
  width?: ButtonWidth;
  /**
   * Whether to show a loading spinner instead of the `children`.
   */
  isLoading?: boolean;
  /**
   * Whether the button is disabled.
   */
  disabled?: boolean;
  /**
   * The styledAs prop for the text inside the button.
   */
  textStyledAs?: StyledAs;
  /**
   * The weight of the text inside the button.
   */
  textWeight?: number;
  /**
   * Icon button variant. TODO: move this to BaseIconButtonProps
   */
  iconButtonVariant?: IconButtonVariant;
  /**
   * When the button is disabled, show a tooltip with this content on hover.
   * Only applies when both `disabled` is true and this string is non-empty.
   */
  disabledButtonTooltipContent?: string;
};

export type ButtonProps = BaseButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>;
export type HtmlLinkProps = BaseButtonProps & React.AnchorHTMLAttributes<HTMLAnchorElement>;
export type RouterLinkProps = BaseButtonProps & RouterLinkPropsType;

/**
 * Standard button component, for all your clickity needs.
 *
 * Will render as:
 * - a react-router `Link` if `to` prop is provided.
 * - an `a` if `href` prop is provided.
 * - a `button` if no `to` or `href` prop is provided.
 */
const Button = ({
  children,
  className = '',
  width = 'hug',
  variant = 'primary',
  size = 'lg',
  isLoading = false,
  disabled = false,
  textStyledAs = 'bodySmallSpaceGrotesk',
  textWeight = 500,
  disabledButtonTooltipContent,
  ...rest
}: ButtonProps | HtmlLinkProps | RouterLinkProps) => {
  const isRouterLink = 'to' in rest;
  const isHtmlLink = 'href' in rest;

  const isDisabled = isLoading || disabled;
  const showDisabledTooltip = disabled && disabledButtonTooltipContent;

  const buttonElement = (
    <StyledButton
      renderedAs={isRouterLink ? Link : isHtmlLink ? 'a' : 'button'}
      styledAs={textStyledAs}
      weight={textWeight}
      $width={width}
      $variant={variant}
      $size={size}
      className={`${isLoading ? 'loading' : ''} ${className}`}
      disabled={isDisabled}
      {...rest}
    >
      {/* TODO: make loading spinner 20x20px in buttons */}
      {isLoading ? <LoadingSpinner light={variant === 'primary'} height="fit-content" /> : children}
    </StyledButton>
  );

  if (showDisabledTooltip) {
    return (
      <Tooltip content={disabledButtonTooltipContent} childNotInteractive>
        {buttonElement}
      </Tooltip>
    );
  }

  return buttonElement;
};

export default Button;
